import {
  Box,
  Chip,
  InputAdornment,
  Stack,
  TextField,
  Tooltip
} from '@mui/material'
import {
  DataGrid,
  type DataGridProps,
  type GridColDef,
  GridOverlay,
  type GridRowParams
} from '@mui/x-data-grid'
import React, { useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'

import { TruncatedTextWithTooltip } from 'components/common/TruncatedTextWithTooltip'
import Icon from 'components/Icon'
import { CloneTestSuiteButton } from 'components/test-suites/CloneTestSuiteButton'
import { DeleteTestSuite } from 'components/test-suites/list/DeleteTestSuite'
import { getTestSuitesUrls } from 'components/test-suites/test-suites-urls-utils'
import {
  getCanCreateAndEditTestSuites,
  isGremlinOfficialTestSuite
} from 'components/test-suites/test-suites-utils'
import useAuth from 'hooks/use-auth'
import useSearch from 'hooks/use-search'
import { useTranslation } from 'hooks/use-translation'
import toast from 'lib/toast'
import theme from 'theme'
import { type TestSuite } from 'types/api/test-suites/test-suites'
import { type ReactQueryFetchFunction } from 'types/react-query'
import {
  getGridStyles,
  safeRowClick
} from 'utils/mui-components/data-grid-utils'

const EmptyState: React.FC = () => {
  const { trans } = useTranslation()
  return (
    <GridOverlay>
      <Stack
        data-test-test-suites-empty-state
        alignItems="center"
        direction="column"
      >
        {trans('test_suites.list.empty_text')}
      </Stack>
    </GridOverlay>
  )
}

export const TestSuitesTable: React.FC<{
  testSuites: TestSuite[]
  fetchTestSuites: ReactQueryFetchFunction<TestSuite[]>
}> = ({ testSuites, fetchTestSuites }) => {
  const navigate = useNavigate()
  const { hasEndpointAccess } = useAuth()
  const testSuitesUrls = getTestSuitesUrls()

  const canCreateAndEditTestSuites =
    getCanCreateAndEditTestSuites(hasEndpointAccess)

  const { trans } = useTranslation()
  const { currentTeamId } = useAuth()

  const handleRowClick: DataGridProps['onRowClick'] = (
    params: GridRowParams<TestSuite>,
    event
  ) => {
    safeRowClick(event, () =>
      navigate(testSuitesUrls.view(params.row.identifier))
    )
  }

  const tableColumns: GridColDef<TestSuite>[] = [
    {
      field: 'name',
      headerName: trans('test_suite'),
      flex: 4,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      disableReorder: true,
      renderCell: (params) => {
        const testSuite = params.row

        return (
          <Link
            data-test-test-suite-link={testSuite.identifier}
            data-track-action="view-test-suite"
            data-track-category="test-suites"
            data-track-on="click"
            state={{ testSuite }}
            to={testSuitesUrls.view(testSuite.identifier)}
          >
            <TruncatedTextWithTooltip
              tooltipProps={{ title: testSuite.name }}
              variant="body2"
            >
              {params.row.name}
            </TruncatedTextWithTooltip>
          </Link>
        )
      }
    },
    {
      field: '_numTeamsUsing',
      headerName: trans('test_suite.list.headers.teams_using'),
      flex: 2,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      disableReorder: true,
      renderCell: (params) => {
        const isCurrentTeamUsing =
          params.row.targetTeamIds?.includes(currentTeamId)

        const numberOfTeamsUsing = params.row.targetTeamIds?.length ?? 0
        const label = isCurrentTeamUsing
          ? `${
              numberOfTeamsUsing - 1 > 0 ? `${numberOfTeamsUsing - 1} + ` : ''
            }${trans('test_suites.list.current_team_test_suite_chip')}`
          : numberOfTeamsUsing

        return (
          <Tooltip
            arrow
            placement="right"
            title={
              isCurrentTeamUsing
                ? trans('test_suites.list.current_team_test_suite_tooltip')
                : ''
            }
          >
            <Chip
              icon={
                <Icon
                  css={{
                    fontSize: theme.fontSizes[2]
                  }}
                  icon="users"
                />
              }
              label={label}
              sx={{
                '& .MuiChip-icon': {
                  mx: 1
                },
                '& .MuiChip-label': {
                  padding: 0,
                  mr: 1
                }
              }}
              variant="outlined"
            />
          </Tooltip>
        )
      }
    }
  ]

  const actionsColumn: GridColDef<TestSuite> = {
    field: '_actions',
    headerName: trans('actions'),
    flex: 2,
    sortable: false,
    filterable: false,
    disableColumnMenu: true,
    disableReorder: true,
    renderCell: (params) => {
      const isOfficial = isGremlinOfficialTestSuite(params.row)

      return (
        <Stack direction="row" gap={2} sx={{ alignItems: 'center' }}>
          <CloneTestSuiteButton testSuite={params.row} />
          {!isOfficial && (
            <DeleteTestSuite
              testSuite={params.row}
              onChanged={async () => {
                toast({
                  type: 'success',
                  text: trans('test_suites.list.delete.success', {
                    name: params.row.name
                  })
                })
                await fetchTestSuites()
              }}
            />
          )}
        </Stack>
      )
    }
  }

  if (canCreateAndEditTestSuites) {
    tableColumns.push(actionsColumn)
  }

  // search setup
  const SUITES_PER_PAGE = 10
  const isSearchAvailable = testSuites.length > SUITES_PER_PAGE

  const [searchInputValue, setSearchInputValue] = useState('')
  const searchMapFn = (testSuite: TestSuite) => {
    const values = [testSuite.name]
    return values.filter(Boolean)
  }
  const searchTestSuites = useSearch(testSuites, searchMapFn)
  const filteredTestSuites = useMemo(
    () => searchTestSuites(searchInputValue),
    [searchInputValue, searchTestSuites]
  )

  const handleSearchInputChange = ({ target: { value } }) => {
    setSearchInputValue(String(value))
  }

  return (
    <Box mt={2}>
      {isSearchAvailable && (
        <TextField
          data-test-test-suite-search
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Icon icon="search" />
              </InputAdornment>
            )
          }}
          label={trans('search')}
          size="small"
          sx={{ mb: 2 }}
          value={searchInputValue}
          onChange={handleSearchInputChange}
        />
      )}
      <Box>
        <DataGrid<TestSuite>
          autoHeight
          disableSelectionOnClick
          columns={tableColumns}
          components={{
            NoRowsOverlay: EmptyState
          }}
          getRowId={(row) => row.identifier}
          rows={filteredTestSuites}
          sx={getGridStyles({ clickableRow: true })}
          onRowClick={handleRowClick}
        />
      </Box>
    </Box>
  )
}
